import { DateTime } from '@cast/shared-types';
import { ClusterReliabilityMetrics } from '@cast/types';

import { WorkloadReliabilityMetricsQueryData } from 'hooks/queries/cost/useWorkloadReliabilityMetricsQuery';
import { DoublyLinkedDatapoint } from 'types/charts';
import { DataGranularity } from 'types/cost-report';
import { TimeSeriesEntryWithTicks } from 'types/metrics';
import { attachDoubleLinks, attachTimeTicks } from 'utils/chart';
import { fillGaps } from 'utils/metrics';

import {
  RELIABILITY_METRICS,
  ReliabilityMetricKey,
} from '../_constants/reliabilityMetrics';

export type ReliabilityMetricsData =
  | ClusterReliabilityMetrics
  | WorkloadReliabilityMetricsQueryData;

export type ReliabilityDatapoint = DoublyLinkedDatapoint<
  TimeSeriesEntryWithTicks<Partial<Record<ReliabilityMetricKey, number | null>>>
>;

export const buildReliabilityChartData = (
  data: ReliabilityMetricsData | undefined,
  dateRange: { from: DateTime; to: DateTime },
  granularity: DataGranularity
): ReliabilityDatapoint[] => {
  const rows = new Map<
    string,
    Omit<ReliabilityDatapoint, 'chartTimestampUnix'>
  >();

  RELIABILITY_METRICS.forEach(({ key }) => {
    data?.[key]?.values?.forEach(({ timestamp, value }) => {
      if (!timestamp || value === undefined) {
        return;
      }

      rows.set(timestamp, {
        ...(rows.get(timestamp) ?? { timestamp }),
        [key]: value,
      });
    });
  });

  const sorted = [...rows.values()].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const nullFiller = RELIABILITY_METRICS.reduce<
    Partial<Record<ReliabilityMetricKey, null>>
  >((acc, { key }) => ({ ...acc, [key]: null }), {});

  const filled = fillGaps({
    timeSeries: sorted,
    dateRange,
    filler: nullFiller,
    unit: granularity.unit,
    step: granularity.step,
    fillPartial: true,
  });

  return attachDoubleLinks(attachTimeTicks(filled));
};

export const hasReliabilityData = (data?: ReliabilityMetricsData): boolean => {
  if (!data) {
    return false;
  }

  return RELIABILITY_METRICS.some(({ key }) => {
    const metric = data[key];

    if (!metric) {
      return false;
    }

    const hasValues =
      metric.values?.some(
        ({ timestamp, value }) => !!timestamp && value !== undefined
      ) ?? false;

    if (data.noDataReason) {
      return hasValues;
    }

    return metric.summary !== undefined || hasValues;
  });
};
